import { FiAlertTriangle, FiArrowLeft, FiChevronLeft, FiChevronRight, FiGrid, FiInbox, FiPieChart, FiDollarSign, FiSearch } from "react-icons/fi";
import { Link } from "react-router-dom";
import { KIND_META, STATUS_META } from "./contribModel";
import "./contrib.css";

const KIND_ICON = { savings: FiDollarSign, shares: FiPieChart, other: FiGrid };

export function KindChip({ kind = "other", small = false }) {
  const Icon = KIND_ICON[kind] || FiGrid;
  return (
    <span className={`cb-kind tone-${(KIND_META[kind] || KIND_META.other).tone} ${small ? "sm" : ""}`}>
      <Icon size={small ? 14 : 18} aria-hidden="true" />
    </span>
  );
}

export function Pill({ tone = "grey", children }) {
  return <span className={`cb-pill tone-${tone}`}>{children}</span>;
}

export function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.contributed;
  return <Pill tone={meta.tone}>{meta.label}</Pill>;
}

/** A thin bar; `value` is 0-100. Hidden from assistive tech unless it has a label. */
export function Progress({ value, tone = "primary", label }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`cb-bar tone-${tone}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={v} aria-label={label || `${v}%`}>
      <span style={{ width: `${v}%` }} />
    </div>
  );
}

export function Tile({ icon: Icon, tone = "grey", label, value, sub, quiet }) {
  return (
    <div className={`cb-tile tone-${tone} ${quiet ? "quiet" : ""}`}>
      {Icon ? (
        <span className="ico">
          <Icon size={16} aria-hidden="true" />
        </span>
      ) : null}
      <div className="v">
        {value}
        {sub ? <small> {sub}</small> : null}
      </div>
      <div className="l">{label}</div>
    </div>
  );
}

export function PageHeader({ title, sub, backTo, backLabel = "Back", actions }) {
  return (
    <div className="cb-head">
      <div className="grow">
        {backTo ? (
          <Link to={backTo} className="cb-crumb">
            <FiArrowLeft size={14} aria-hidden="true" /> {backLabel}
          </Link>
        ) : null}
        <h1 className="cb-title">{title}</h1>
        {sub ? <p className="cb-mute">{sub}</p> : null}
      </div>
      {actions ? <div className="cb-actions">{actions}</div> : null}
    </div>
  );
}

export function Tabs({ tabs, active, onChange, label = "Sections" }) {
  return (
    <div className="cb-tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={active === t.key} className={`cb-tab ${active === t.key ? "on" : ""}`} onClick={() => onChange(t.key)}>
          {t.label}
          {t.count !== undefined && t.count !== null ? <span className="cb-count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Chips({ items, active, onChange, label }) {
  return (
    <div className="cb-chips" role="group" aria-label={label}>
      {items.map((c) => (
        <button key={c.key} type="button" className={`cb-chip ${active === c.key ? "on" : ""}`} aria-pressed={active === c.key} onClick={() => onChange(c.key)}>
          {c.label}
          {c.count !== undefined ? ` ${c.count}` : ""}
        </button>
      ))}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, label }) {
  return (
    <label className="cb-search">
      <FiSearch size={16} aria-hidden="true" />
      <span className="sr-only" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{label || placeholder}</span>
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}

/** "Nothing here" and "could not load" are different things and must never look alike. */
export function Notice({ tone = "primary", icon: Icon, title, children, actionLabel, onAction, actionTo }) {
  const I = Icon || (tone === "amber" || tone === "red" ? FiAlertTriangle : FiInbox);
  return (
    <div className={`cb-notice tone-${tone}`} role={tone === "amber" || tone === "red" ? "alert" : undefined}>
      <span className="ico">
        <I size={20} aria-hidden="true" />
      </span>
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {actionLabel && actionTo ? (
        <Link to={actionTo} className="cb-btn primary sm">
          {actionLabel}
        </Link>
      ) : null}
      {actionLabel && onAction ? (
        <button type="button" className="cb-btn primary sm" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export function Skeleton({ rows = 4 }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="cb-skel" />
      ))}
    </div>
  );
}

export function Pager({ page, pages, total, noun = "results", onPage, perPage = 20 }) {
  if (!total) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);
  return (
    <div className="cb-pager">
      <span>
        Showing {from} to {to} of {total} {noun}
      </span>
      {pages > 1 ? (
        <div className="btns">
          <button type="button" className="cb-btn sm" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
            <FiChevronLeft size={16} aria-hidden="true" />
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button type="button" className="cb-btn sm" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page">
            <FiChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Person({ name, sub, photo }) {
  const initials = String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "M";
  return (
    <div className="cb-person">
      <span className="cb-avatar">{photo && !String(photo).endsWith("avatar.svg") ? <img src={photo} alt="" /> : <span aria-hidden="true">{initials}</span>}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 600, color: "var(--cb-ink)", overflowWrap: "anywhere" }}>{name}</span>
        {sub ? <span className="cb-sub">{sub}</span> : null}
      </span>
    </div>
  );
}
